// Inline slip-duration pill. `late` (red) signals an active problem; `deliveredLate` (amber) records history.
//
// The visible ▲ glyph is verbalised by screen readers as "up-pointing
// triangle", which carries none of the meaning it carries visually, so the
// pill supplies its own spoken phrasing.
//
// That phrasing is VISUALLY-HIDDEN TEXT, not an aria-label. ARIA prohibits a
// name on a generic element, which is what this role-less span is: the label
// is flagged by automated checks and dropped outright by VoiceOver, so the
// listener would be left with the triangle after all. The visible run is
// hidden from assistive tech instead, and the sentence beside it is what gets
// read. Mirrors DeliverableSlipTag.

import { clsx } from "clsx";

export type SlipTagVariant = "late" | "deliveredLate";

export interface SlipTagProps {
  variant: SlipTagVariant;
  days: number;
  /** Headline form (`12 days late`) used inside summary strip cards. Inline form
   *  (`▲ 12d late` / `▲ delivered 12d late`) is the default for table rows. */
  headline?: boolean;
}

const VARIANT_CLASS: Record<SlipTagVariant, string> = {
  late: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  deliveredLate: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

function renderText(variant: SlipTagVariant, days: number, headline: boolean): string {
  if (headline) {
    return variant === "late" ? `${days} days late` : `delivered ${days} days late`;
  }
  return variant === "late" ? `▲ ${days}d late` : `▲ delivered ${days}d late`;
}

function renderSpokenText(variant: SlipTagVariant, days: number): string {
  const unit = days === 1 ? "day" : "days";
  return variant === "late"
    ? `Overdue by ${days} ${unit}`
    : `Delivered ${days} ${unit} late`;
}

export default function SlipTag({ variant, days, headline = false }: SlipTagProps) {
  return (
    <span
      data-testid={`slip-tag-${variant}`}
      data-variant={variant}
      className={clsx(
        "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-caption font-medium",
        VARIANT_CLASS[variant],
      )}
    >
      <span aria-hidden="true">{renderText(variant, days, headline)}</span>
      <span className="sr-only">{renderSpokenText(variant, days)}</span>
    </span>
  );
}
